import { FC, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { BlockProps } from '../props'
import BlockPublishAds, {
  BlockPublishAdsProps,
} from '../../../components/blocks/BlockPublishAds'
import SectionWrapper from '../../../components/SectionWrapper'
import { ActionButtonVariant } from '../../../components/ActionButton/styled'
import { ActionButtonTransformer } from '../../Transformers/LinkTransformer'
import { useServiceRequest } from '../../../hooks/useServiceRequest'
import { actions, selectors } from '../../../redux'
import { Classified, Status } from '../../../rest/types/Classified'
import { classifiedCardTransformer } from '../../../rest/transformers/classifiedTransformer'
import { ActionButtonProps } from '../../../components/ActionButton'
import { AdsCardProps } from '../../../components/AdsCard'
import { useSagaTakeEvery } from '../../../hooks/useSaga'
import Router from '../../../routes/Router'
import routes from '../../../routes/definitions'
import { AdsCardVariant } from '../../../components/AdsCard/styled'

import { PublishAdsProps } from './props'

const PublishAds: FC<BlockProps<PublishAdsProps>> = ({ data }) => {
  const { t } = useTranslation()
  const user = useSelector(selectors.auth.user)

  const [publishAdsResponse, getPublishAds] = useServiceRequest(
    selectors.classifieds.getPublishAds,
    actions.classifieds.getPublishAdsRequest,
    actions.classifieds.getPublishAdsReset
  )

  const [{ pending }, create] = useServiceRequest(
    selectors.classifieds.create,
    actions.classifieds.createRequest,
    actions.classifieds.createReset
  )

  useEffect(() => {
    getPublishAds({
      sort: 'updatedAt:desc',
      filters: { classifiedStatus: Status.Archived, filled: true },
      pagination: {
        limit: 4,
      },
      populate: {
        classifiedCategory: {
          fields: ['name', 'form'],
        },
        classified_views: {
          fields: ['id'],
        },
        classified_answers: {
          fields: ['id'],
        },
      },
    })
  }, [getPublishAds])

  const props: BlockPublishAdsProps = useMemo(
    () => ({
      title: data.title ?? '',
      text: t('block_publish_small_text'),
      lastAds:
        publishAdsResponse?.data?.map(
          (data: Classified) =>
            classifiedCardTransformer({
              data,
              t,
              colorVariant: AdsCardVariant.secondary,
              contentVariant: 'withoutLink',
              latestPublishLabel: t('block_ads_related_latest_publish_label'),
            }) as AdsCardProps
        ) ?? [],
      card: {
        title: t('block_publish_title'),
        text: t('block_publish_description'),
        buttons: {
          items: [
            ActionButtonTransformer(data?.link) as ActionButtonProps,
            ...(user?.id
              ? [
                  {
                    text: t('block_publish_button_publish_ads'),
                    variant: ActionButtonVariant.quaternary,
                    isLoading: pending,
                    onClick: () => {
                      create({
                        user: user?.id,
                      })
                    },
                  },
                ]
              : []),
          ],
        },
      },
    }),
    [
      data.title,
      data?.link,
      t,
      publishAdsResponse?.data,
      pending,
      create,
      user?.id,
    ]
  )

  useSagaTakeEvery(
    actions.classifieds.createSuccess,
    useCallback(({ payload }) => {
      Router.pushRoute(routes.classifiedCreateOrUpdate, {
        id: payload?.id,
      })
    }, [])
  )

  return (
    <SectionWrapper spacing="big">
      <BlockPublishAds {...props} />
    </SectionWrapper>
  )
}
export default PublishAds
